import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useContacts } from '../../context/ContactContext';
import { searchUsers } from '../../services/userSearchService';
import { sendContactRequest, acceptContactRequest, rejectContactRequest, blockUser, unblockUser, createRequesterContact } from '../../services/contactService';
import { getUserContactCode, createContactCode, getTimeRemaining, formatTimeRemaining, validateContactCode } from '../../services/contactCodeService';
import { db } from '../../config/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import ContactDetailModal from './ContactDetailModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

/**
 * ContactsSection - Manage contacts within Chat tab
 * Shows: search for contacts, pending requests, accepted contacts, blocked users
 */
export default function ContactsSection() {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const { pendingRequests, contacts, blockedUsers, loading, deleteContact: deleteContactService, updateContactDetails } = useContacts();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeSubsection, setActiveSubsection] = useState('search'); // search, requests, contacts, blocked
  const [sendingRequest, setSendingRequest] = useState({});
  const [processingRequest, setProcessingRequest] = useState({});
  const [myContactCode, setMyContactCode] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loadingCode, setLoadingCode] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  // Modal states
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);

  // Load user's contact code
  useEffect(() => {
    if (!user?.uid) {
      setMyContactCode(null);
      return;
    }

    const loadContactCode = async () => {
      setLoadingCode(true);
      try {
        const code = await getUserContactCode(user.uid);
        if (code) {
          setMyContactCode(code.code);
          const remaining = getTimeRemaining(code.expiresAt);
          setTimeRemaining(remaining);
        } else {
          setMyContactCode(null);
        }
      } catch (error) {
        console.error('Error loading contact code:', error);
      } finally {
        setLoadingCode(false);
      }
    };

    loadContactCode();
  }, [user?.uid]);

  // Update timer for contact code expiration
  useEffect(() => {
    if (!myContactCode) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [myContactCode]);

  // Listen for accepted requests and auto-create requester's contact
  useEffect(() => {
    if (!user?.uid) return;

    // Listen to requests sent BY the current user
    const sentRequestsQuery = query(
      collection(db, 'contactRequests'),
      where('fromUserId', '==', user.uid),
      where('status', '==', 'accepted')
    );

    const unsubscribe = onSnapshot(sentRequestsQuery, (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const request = doc.data();
        // Auto-create requester's contact document when their request is accepted
        createRequesterContact(doc.id).catch(err => {
          console.error('Failed to auto-create requester contact:', err);
        });
      });
    }, (error) => {
      console.error('Error listening to sent requests:', error);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const handleGenerateContactCode = async () => {
    setGeneratingCode(true);
    try {
      const result = await createContactCode(user.uid, userProfile.displayName);
      setMyContactCode(result.code);
      const remaining = getTimeRemaining(result.expiresAt);
      setTimeRemaining(remaining);
      toast.success('Contact code generated successfully');
    } catch (error) {
      console.error('Error generating contact code:', error);
      toast.error('Failed to generate contact code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = () => {
    if (!myContactCode) return;
    navigator.clipboard.writeText(myContactCode);
    toast.success('Code copied to clipboard!');
  };

  // Search for users to add as contacts
  const handleSearch = useCallback(async (searchTerm) => {
    setSearchQuery(searchTerm);

    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const trimmed = searchTerm.trim().toUpperCase();
      const allResults = [];

      // Check if it's a 6-character contact code
      if (/^[A-Z0-9]{6}$/.test(trimmed)) {
        try {
          const profile = await validateContactCode(trimmed);
          if (profile) {
            allResults.push({
              id: profile.userId,
              displayName: profile.displayName,
              firstName: profile.firstName,
              lastName: profile.lastName,
              email: profile.email,
              phone: profile.phone,
              countryCode: profile.countryCode,
              photoURL: profile.photoURL,
              isFromCode: true
            });
          }
        } catch (error) {
          console.error('Error validating contact code:', error);
        }
      }

      // Also do regular search
      const results = await searchUsers(searchTerm);
      allResults.push(...results);

      // Filter out current user and already-contacted users
      const contactedUserIds = new Set([
        ...contacts.map(c => c.contactId),
        ...pendingRequests.map(r => r.toUserId === user.uid ? r.fromUserId : r.toUserId),
        user.uid
      ]);

      const filtered = allResults.filter(u => !contactedUserIds.has(u.id));

      // Deduplicate by id
      const seen = new Set();
      const deduped = filtered.filter(u => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });

      setSearchResults(deduped);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [user.uid, contacts, pendingRequests]);

  // Send contact request
  const handleSendRequest = useCallback(async (toUserId) => {
    setSendingRequest(prev => ({ ...prev, [toUserId]: true }));
    try {
      // Find the user in search results to check if from code
      const foundUser = searchResults.find(u => u.id === toUserId);

      // Determine search source based on how contact was found
      let source = 'search';
      if (foundUser?.isFromCode) {
        source = 'code';
      } else if (searchQuery.includes('@')) {
        source = 'email_search';
      } else if (/\d/.test(searchQuery) && searchQuery.length >= 3) {
        source = 'phone_search';
      } else {
        source = 'name_search';
      }

      await sendContactRequest(user.uid, toUserId, source);
      // Remove from search results
      setSearchResults(prev => prev.filter(u => u.id !== toUserId));
      setSearchQuery('');
      toast.success('Contact request sent!');
    } catch (error) {
      console.error('Error sending contact request:', error);
      toast.error('Failed to send contact request');
    } finally {
      setSendingRequest(prev => ({ ...prev, [toUserId]: false }));
    }
  }, [user.uid, searchQuery, searchResults, toast]);

  // Accept contact request
  const handleAcceptRequest = useCallback(async (requestId) => {
    setProcessingRequest(prev => ({ ...prev, [requestId]: true }));
    try {
      await acceptContactRequest(requestId);
    } catch (error) {
      console.error('Error accepting request:', error);
    } finally {
      setProcessingRequest(prev => ({ ...prev, [requestId]: false }));
    }
  }, []);

  // Reject contact request
  const handleRejectRequest = useCallback(async (requestId) => {
    setProcessingRequest(prev => ({ ...prev, [requestId]: true }));
    try {
      await rejectContactRequest(requestId);
    } catch (error) {
      console.error('Error rejecting request:', error);
    } finally {
      setProcessingRequest(prev => ({ ...prev, [requestId]: false }));
    }
  }, []);

  // Block user
  const handleBlockUser = useCallback(async (userId) => {
    setProcessingRequest(prev => ({ ...prev, [userId]: true }));
    try {
      await blockUser(user.uid, userId);
    } catch (error) {
      console.error('Error blocking user:', error);
    } finally {
      setProcessingRequest(prev => ({ ...prev, [userId]: false }));
    }
  }, [user.uid]);

  // Unblock user
  const handleUnblockUser = useCallback(async (userId) => {
    setProcessingRequest(prev => ({ ...prev, [userId]: true }));
    try {
      await unblockUser(user.uid, userId);
    } catch (error) {
      console.error('Error unblocking user:', error);
    } finally {
      setProcessingRequest(prev => ({ ...prev, [userId]: false }));
    }
  }, [user.uid]);

  // Open contact detail modal
  const handleEditContact = useCallback((contact) => {
    setSelectedContact(contact);
    setShowDetailModal(true);
  }, []);

  // Close contact detail modal
  const handleCloseDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedContact(null);
  }, []);

  // Open delete confirmation modal
  const handleDeleteClick = useCallback((contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  }, []);

  // Close delete confirmation modal
  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedContact(null);
  }, []);

  // Confirm delete contact
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedContact) return;

    setDeletingContact(true);
    try {
      await deleteContactService(selectedContact.contactId);
      toast.success('Contact deleted successfully');
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setDeletingContact(false);
    }
  }, [selectedContact, deleteContactService, toast]);

  return (
    <div className="space-y-6">
      {/* Subsection Tabs */}
      <div className="flex gap-2 border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
        <button
          onClick={() => setActiveSubsection('search')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeSubsection === 'search'
              ? 'text-[#06c] dark:text-[#06c] border-[#06c]'
              : 'text-[#86868b] dark:text-[#8e8e93] border-transparent hover:text-[#1d1d1f] dark:hover:text-white'
          }`}
        >
          Find Contacts
        </button>
        {pendingRequests.length > 0 && (
          <button
            onClick={() => setActiveSubsection('requests')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors relative ${
              activeSubsection === 'requests'
                ? 'text-[#06c] dark:text-[#06c] border-[#06c]'
                : 'text-[#86868b] dark:text-[#8e8e93] border-transparent hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            Requests
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {pendingRequests.length}
            </span>
          </button>
        )}
        {contacts.length > 0 && (
          <button
            onClick={() => setActiveSubsection('contacts')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeSubsection === 'contacts'
                ? 'text-[#06c] dark:text-[#06c] border-[#06c]'
                : 'text-[#86868b] dark:text-[#8e8e93] border-transparent hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            My Contacts ({contacts.length})
          </button>
        )}
        {blockedUsers.length > 0 && (
          <button
            onClick={() => setActiveSubsection('blocked')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeSubsection === 'blocked'
                ? 'text-red-500 dark:text-red-400 border-red-500'
                : 'text-[#86868b] dark:text-[#8e8e93] border-transparent hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            Blocked ({blockedUsers.length})
          </button>
        )}
      </div>

      {/* Search Section */}
      {activeSubsection === 'search' && (
        <div className="space-y-4">
          {/* My Contact Code - Compact Integrated Section */}
          {loadingCode ? (
            <div className="flex justify-center py-3">
              <div className="w-4 h-4 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
            </div>
          ) : myContactCode ? (
            <div className="flex items-center gap-3 p-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
              <div className="flex-1">
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mb-1">Your Code</p>
                <code className="text-sm font-bold text-[#1d1d1f] dark:text-white font-mono tracking-widest">
                  {myContactCode}
                </code>
              </div>
              <button
                onClick={handleCopyCode}
                className="p-2 hover:bg-white dark:hover:bg-[#1c1c1e] rounded-lg transition-colors flex-shrink-0"
                title="Copy code"
              >
                <svg className="w-4 h-4 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              {timeRemaining > 0 && (
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-[#86868b] dark:text-[#6e6e73]">{formatTimeRemaining(timeRemaining)}</p>
                </div>
              )}
              {timeRemaining === 0 && (
                <button
                  onClick={handleGenerateContactCode}
                  disabled={generatingCode}
                  className="text-xs text-[#06c] hover:text-[#0055d4] font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                  {generatingCode ? '...' : 'New'}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleGenerateContactCode}
              disabled={generatingCode}
              className="w-full py-2.5 px-4 bg-[#06c] hover:bg-[#0055d4] text-white font-semibold rounded-lg transition-colors text-sm active:scale-95"
            >
              {generatingCode ? 'Generating...' : 'Generate Contact Code'}
            </button>
          )}

          {/* Search Bar */}
          <div className="space-y-3">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name, email, phone, or code..."
                className="w-full pl-12 pr-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-[#d2d2d7] dark:border-[#424245] rounded-full text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:border-[#06c] transition-colors"
              />
            </div>
          </div>

          {searching && (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
            </div>
          )}

          {searchQuery.length < 2 && !searching && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-[#d2d2d7] dark:text-[#424245] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-[#86868b] dark:text-[#8e8e93] text-sm">Search by name, email, phone, or enter a 6-character code</p>
            </div>
          )}

          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#86868b] dark:text-[#8e8e93] text-sm">No results found</p>
              <p className="text-[#86868b]/70 dark:text-[#8e8e93]/70 text-xs mt-1">
                Try searching by email, phone, or check if their profile is public
              </p>
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-3">
              {searchResults.map(user => (
                <div key={user.id} className="p-4 bg-white dark:bg-[#1c1c1e] rounded-lg border border-[#d2d2d7] dark:border-[#2c2c2e] hover:border-[#06c] dark:hover:border-[#06c] transition-all">
                  <div className="flex gap-3 mb-3">
                    {/* Avatar / Profile Picture */}
                    {user.showProfilePic !== false && user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#06c] to-[#0055d4] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-sm">
                          {(user.firstName || user.displayName || user.email)?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}

                    {/* User Header with Add Button */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#1d1d1f] dark:text-white truncate">
                            {user.displayName || user.name || 'User'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSendRequest(user.id)}
                          disabled={sendingRequest[user.id]}
                          className="px-3 py-1.5 bg-[#06c] hover:bg-[#0055d4] text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 whitespace-nowrap"
                        >
                          {sendingRequest[user.id] ? (
                            <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            'Add'
                          )}
                        </button>
                      </div>

                      {/* Profile Info - Privacy Respecting */}
                      <div className="space-y-1">
                        {/* Name */}
                        {user.showName !== false && user.firstName && user.lastName && (
                          <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">
                            {user.firstName} {user.lastName}
                          </p>
                        )}

                        {/* Email */}
                        {user.showEmail !== false && user.email && (
                          <p className="text-xs text-[#86868b] dark:text-[#8e8e93] truncate">
                            {user.email}
                          </p>
                        )}

                        {/* Phone */}
                        {user.showPhone !== false && user.phone && (
                          <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">
                            {user.countryCode} {user.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Requests Section */}
      {activeSubsection === 'requests' && (
        <div className="space-y-2">
          {pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#86868b] dark:text-[#8e8e93] text-sm">No pending requests</p>
            </div>
          ) : (
            pendingRequests.map(request => (
              <div key={request.id} className="flex items-center gap-3 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">
                    {(request.fromUserName || request.fromUserId)?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>

                {/* Request Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-[#1d1d1f] dark:text-white truncate">
                      {request.fromUserName || 'Unknown User'}
                    </p>
                    {request.source && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 whitespace-nowrap">
                        {request.source === 'code' ? 'Code' :
                         request.source === 'email_search' ? 'Email' :
                         request.source === 'phone_search' ? 'Phone' :
                         request.source === 'name_search' ? 'Name' :
                         'Search'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#86868b] dark:text-[#8e8e93] truncate">
                    {request.reason || 'Would like to connect'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleAcceptRequest(request.id)}
                    disabled={processingRequest[request.id]}
                    className="px-3 py-2 bg-[#06c] text-white rounded-full text-xs font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingRequest[request.id] ? '...' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleRejectRequest(request.id)}
                    disabled={processingRequest[request.id]}
                    className="px-3 py-2 bg-[#e5e5ea] dark:bg-[#424245] text-[#1d1d1f] dark:text-white rounded-full text-xs font-medium hover:bg-[#d2d2d7] dark:hover:bg-[#545456] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingRequest[request.id] ? '...' : 'Decline'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* My Contacts Section */}
      {activeSubsection === 'contacts' && (
        <div className="space-y-2">
          {contacts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2">No contacts</h3>
              <p className="text-[#86868b] dark:text-[#8e8e93] text-sm mb-4">
                Add contacts by searching or sharing your code
              </p>
              <button
                onClick={() => setActiveSubsection('search')}
                className="px-6 py-2.5 bg-[#06c] text-white rounded-full font-medium hover:bg-[#0055d4] transition-colors text-sm"
              >
                Find Contacts
              </button>
            </div>
          ) : (
            contacts.map(contact => (
              <div key={contact.id} className="flex items-start gap-3 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl hover:bg-[#e5e5ea] dark:hover:bg-[#424245] transition-colors">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#06c] to-[#0088cc] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white font-semibold text-sm">
                    {(contact.nickname || contact.contactName || contact.contactId)?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-[#1d1d1f] dark:text-white truncate">
                      {contact.nickname || contact.contactName || 'Unknown User'}
                    </p>
                    {contact.source && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 whitespace-nowrap">
                        {contact.source === 'code' ? 'Code' :
                         contact.source === 'email_search' ? 'Email' :
                         contact.source === 'phone_search' ? 'Phone' :
                         contact.source === 'name_search' ? 'Name' :
                         'Search'}
                      </span>
                    )}
                  </div>
                  {contact.notes && (
                    <p className="text-xs text-[#86868b] dark:text-[#8e8e93] truncate mt-0.5">
                      Notes: {contact.notes.length > 40 ? contact.notes.substring(0, 40) + '...' : contact.notes}
                    </p>
                  )}
                  <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-0.5">
                    {contact.status === 'accepted' ? 'Contact' : 'Archived'}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEditContact(contact)}
                    className="p-2 text-[#06c] hover:bg-[#06c]/10 rounded-full transition-colors"
                    title="Edit contact details"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteClick(contact)}
                    className="p-2 text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
                    title="Delete contact"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleBlockUser(contact.contactId)}
                    disabled={processingRequest[contact.contactId]}
                    className="p-2 text-[#86868b] hover:bg-[#86868b]/10 rounded-full transition-colors disabled:opacity-50"
                    title="Block this user"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Blocked Users Section */}
      {activeSubsection === 'blocked' && (
        <div className="space-y-2">
          {blockedUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#86868b] dark:text-[#8e8e93] text-sm">No blocked users</p>
            </div>
          ) : (
            blockedUsers.map(block => (
              <div key={block.id} className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>

                {/* Blocked User Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1d1d1f] dark:text-white truncate">
                    Blocked User
                  </p>
                  <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">
                    Blocked {block.blockedAt?.toDate?.()?.toLocaleDateString?.() || 'previously'}
                  </p>
                </div>

                {/* Unblock Button */}
                <button
                  onClick={() => handleUnblockUser(block.blockedId)}
                  disabled={processingRequest[block.blockedId]}
                  className="px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50"
                >
                  Unblock
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Contact Detail Modal */}
      {showDetailModal && selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onClose={handleCloseDetailModal}
          onUpdate={updateContactDetails}
          toast={toast}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedContact && (
        <DeleteConfirmationModal
          contact={selectedContact}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          isDeleting={deletingContact}
        />
      )}
    </div>
  );
}
